import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authenticator } from "../Authentication/auth-funcs";
import logger from '../utils/logger';
import { identify } from "../utils/analytics";
import { clearSignedOut } from "../Authentication/sessionEnded";


// Async thunk to fetch user attributes
export const getCognitoAttributes = createAsyncThunk(
  "login/getCognitoAttributes",
  async (_, { rejectWithValue }) => {
    try {
      // getCurrentUser only reads the stored tokens. Asking for attributes with no tokens makes
      // Amplify fetch guest credentials from the identity pool, which allows no guests, so every
      // signed-out page load used to end in a failed (400) Cognito Identity GetId call.
      if (!(await authenticator.getCurrentUser())) {
        return rejectWithValue("Not signed in");
      }
      const attributes = await authenticator.getUserAttributes();
      logger.log("Fetched user attributes:", attributes);
      identify(attributes.sub, {
        email: attributes.email,
        name: attributes.name,
      });
      // Sign in with Apple / Google accounts carry none of `name`, `email` or
      // `email_verified` — the IdP owns those and Cognito never writes them into
      // the user pool. `identities` is present only on such accounts. When the
      // pool holds no `email_verified` at all there is nothing to verify and no
      // address to send a code to, so treat it as verified rather than stranding
      // the user on the email-code screen. Once they set an email of their own,
      // Cognito starts reporting `email_verified` and that answer wins again —
      // otherwise a pending address would silently count as verified forever.
      // For the missing name/email, see redux/userIdentity.js.
      clearSignedOut();
      const isFederated = Boolean(attributes.identities);
      const cognitoTracksVerification = attributes.email_verified !== undefined;
      return {
        userId: attributes.sub,
        username: attributes.name,
        email: attributes.email,
        emailVerified:
          attributes.email_verified === "true" ||
          (isFederated && !cognitoTracksVerification),
        isFederated,
        isNewUser: attributes["custom:firstLogin"] === "true", // Consistent attribute name
      };
    } catch (error) {
      logger.error("Error fetching attributes:", error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  userId: null,
  username: null,
  email: null,
  emailVerified: false,
  isFederated: false,
  isNewUser: false,
  signedIn: false,
  isLoading: false,
  isLoaded: false,
  error: null,
  pendingEmailChange: false,
  // True from a sign-out until the next successful attribute fetch: there is definitely no
  // session, so ProtectedRoute redirects at once instead of showing a spinner.
  sessionEnded: false,
};

export const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    resetLoginState: (state) => {
      Object.assign(state, initialState);
    },
    // The session ended (sign-out, another tab, failed refresh). Without isLoaded the
    // dashboard's ProtectedRoute showed its loading screen forever after Back.
    markSessionEnded: (state) => {
      state.sessionEnded = true;
      state.signedIn = false;
      state.isLoading = false;
      state.isLoaded = true;
    },
    setSignedIn: (state, action) => {
      state.signedIn = action.payload;
    },
    setNotFirstLogin: (state) => {
      state.isNewUser = false; // Set isNewUser to false
      state.emailVerified = true; // Assume email is verified when not a new user
    },
    setPendingEmailChange: (state, action) => {
      state.emailVerified = false;
      state.pendingEmailChange = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCognitoAttributes.pending, (state) => {
        state.isLoading = true;
        state.isLoaded = false;
        state.error = null;
      })
      .addCase(getCognitoAttributes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoaded = true;
        state.userId = action.payload.userId;
        state.username = action.payload.username;
        state.email = action.payload.email;
        state.emailVerified = action.payload.emailVerified;
        state.isFederated = action.payload.isFederated;
        state.isNewUser = action.payload.isNewUser;
        state.signedIn = true;
        state.sessionEnded = false;
      })
      .addCase(getCognitoAttributes.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoaded = true;
        state.error = action.payload || "Failed to load user attributes";
        state.signedIn = false;
      });
  },
});

// Action creators
export const { resetLoginState, markSessionEnded, setSignedIn, setNotFirstLogin, setPendingEmailChange } = loginSlice.actions;

export default loginSlice.reducer;